import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Card,
  CardBody,
  Button,
  Tabs,
  Tab,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import MarketCommentsSidebar from '../../../../common/src/components/market/MarketCommentsSidebar';
import {
  listMarketComments,
  deleteMarketComment,
} from '../../../../common/src/api/market_comments';
import { MarketComment } from '../../../../common/src/utils/types';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import Header from '../../components/layouts/Header';
import {
  EmptyState,
  LoadingState,
  PageLayout,
  useConfirm,
} from '../../components/ui';

const LogbookPage = () => {
  const { t } = useTranslation('logbook');
  const { selectedSpecies } = useDataStore();
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();

  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const confirm = useConfirm();
  const [comments, setComments] = useState<MarketComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const exporterId = userProfile?.exporter?.id;
  const userId = userProfile?.id;
  const speciesId = selectedSpecies?.id;

  // Load comments for table view
  useEffect(() => {
    if (viewMode !== 'table' || !exporterId) return;

    const loadComments = async () => {
      setLoading(true);
      try {
        const data = await listMarketComments({
          species_id: speciesId,
          season: selectedSeason,
          limit: 500,
        });
        setComments(data);
      } catch (error) {
        console.error('Error loading comments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadComments();
  }, [viewMode, exporterId, speciesId, selectedSeason, reloadTrigger]);

  // Format date for display
  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y?.slice(-2)}`;
  };

  // Format datetime for display
  const formatDateTime = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = String(date.getFullYear()).slice(-2);
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${d}/${m}/${y} ${h}:${min}`;
  };

  // Get creator name
  const getCreatorName = (comment: MarketComment): string => {
    if (typeof comment.created_by === 'object' && comment.created_by) {
      const creator = comment.created_by as any;
      return (
        `${creator.first_name || ''} ${creator.last_name || ''}`.trim() || '-'
      );
    }
    return '-';
  };

  // Get editor name (if available)
  const getEditorName = (comment: MarketComment): string => {
    // Note: edited_by is just an ID, we'd need to fetch user data to get name
    // For now, just return the ID or empty
    return comment.edited_by ? String(comment.edited_by) : '-';
  };

  // Format fleet display
  const formatFleet = (fleet?: 'A' | 'M' | null): string => {
    if (!fleet) return '-';
    return fleet === 'A' ? 'Aéreo' : 'Marítimo';
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    if (
      !(await confirm({
        title: t('¿Eliminar este comentario?'),
        isDestructive: true,
      }))
    )
      return;
    try {
      await deleteMarketComment(id);
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    const data = comments.map((c) => ({
      ID: c.id || '',
      'Fecha Desde': c.date_from || '',
      'Fecha Hasta': c.date_to || '',
      Temporada: c.season || '',
      Mercado: c.market_id || '',
      Variedad: c.variety || '',
      Calibre: c.caliber || '',
      Color: c.color || '',
      Cliente: c.seller || '',
      Etiqueta: c.label || '',
      Formato: c.format || '',
      Categoría: c.category || '',
      Referencia: c.exporter_ref || '',
      Contenedor: c.container_number || '',
      Nave: c.ship_name || '',
      Flota: formatFleet(c.fleet),
      'Peso (kg)': c.unit_weight || '',
      'Precio Bulto': c.unit_price || '',
      Moneda: c.local_currency || '',
      Cantidad: c.unit_count || '',
      Comentario: c.content || '',
      'Creado Por': getCreatorName(c),
      'Creado En': c.created_at ? formatDateTime(c.created_at) : '',
      'Actualizado En': c.updated_at ? formatDateTime(c.updated_at) : '',
      Editado: c.is_edited ? 'Sí' : 'No',
      'Editado Por': getEditorName(c),
      'Última Edición': c.last_edited_at
        ? formatDateTime(c.last_edited_at)
        : '',
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bitácora');
    XLSX.writeFile(wb, `bitacora_${selectedSeason || 'todas'}.xlsx`);
  };

  return (
    <PageLayout className='h-full flex flex-col'>
      {/* Header */}
      <Header
        title={t('Consolidado Bitácora')}
        description={t(
          'Registro consolidado de precios y comentarios del mercado de todo tu equipo.'
        )}
      />

      {/* Filters display */}
      <Card className='mb-4' shadow='sm'>
        <CardBody>
          <div className='flex flex-wrap items-center gap-6'>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-default-500'>{t('Especie')}:</span>
              <span className='font-medium'>
                {selectedSpecies?.name || t('No seleccionada')}
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-default-500'>{t('Temporada')}:</span>
              <span className='font-medium'>
                {selectedSeason || t('Todas')}
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* View toggle */}
      <div className='mb-3 flex items-center justify-between'>
        <Tabs
          aria-label={t('Vista')}
          selectedKey={viewMode}
          onSelectionChange={(k) => setViewMode(k as 'cards' | 'table')}
        >
          <Tab
            key='cards'
            title={
              <div className='flex items-center gap-2'>
                <Icon icon='tabler:layout-grid' />
                <span>{t('Lista')}</span>
              </div>
            }
          />
          <Tab
            key='table'
            title={
              <div className='flex items-center gap-2'>
                <Icon icon='tabler:table' />
                <span>{t('Tabla')}</span>
              </div>
            }
          />
        </Tabs>
        {viewMode === 'table' && comments.length > 0 && (
          <Button
            variant='flat'
            onPress={handleExportExcel}
            startContent={
              <Icon icon='mdi:microsoft-excel' className='text-xl' />
            }
          >
            {t('Exportar Excel')}
          </Button>
        )}
      </div>

      {/* Main content */}
      <div className='flex-1 min-h-0 overflow-auto'>
        {!exporterId || !userId ? (
          <EmptyState title={t('No hay exportadora asociada a tu cuenta')} />
        ) : viewMode === 'cards' ? (
          <MarketCommentsSidebar
            isOpen={true}
            onToggle={() => {}}
            viewMode='week'
            speciesId={speciesId}
            season={selectedSeason}
            exporterId={exporterId}
            userId={userId}
            variant='embedded'
            loadAll={true}
            onBitacoraChange={() => setReloadTrigger((n) => n + 1)}
          />
        ) : (
          <Card shadow='sm'>
            <CardBody>
              {loading ? (
                <LoadingState />
              ) : (
                <Table aria-label='Tabla de bitácora' removeWrapper>
                  <TableHeader>
                    <TableColumn>{t('FECHA')}</TableColumn>
                    <TableColumn>{t('TEMPORADA')}</TableColumn>
                    <TableColumn>{t('MERCADO')}</TableColumn>
                    <TableColumn>{t('VARIEDAD')}</TableColumn>
                    <TableColumn>{t('CALIBRE')}</TableColumn>
                    <TableColumn>{t('COLOR')}</TableColumn>
                    <TableColumn>{t('CLIENTE')}</TableColumn>
                    <TableColumn>{t('ETIQUETA')}</TableColumn>
                    <TableColumn>{t('REFERENCIA')}</TableColumn>
                    <TableColumn>{t('CONTENEDOR')}</TableColumn>
                    <TableColumn>{t('NAVE')}</TableColumn>
                    <TableColumn>{t('FLOTA')}</TableColumn>
                    <TableColumn>{t('PRECIO')}</TableColumn>
                    <TableColumn>{t('COMENTARIO')}</TableColumn>
                    <TableColumn>{t('CREADO POR')}</TableColumn>
                    <TableColumn>{t('CREADO EN')}</TableColumn>
                    <TableColumn>{t('ACTUALIZADO EN')}</TableColumn>
                    <TableColumn>{t('EDITADO')}</TableColumn>
                    <TableColumn width={100}>{t('ACCIONES')}</TableColumn>
                  </TableHeader>
                  <TableBody emptyContent={t('No hay comentarios')}>
                    {comments.map((comment) => (
                      <TableRow key={comment.id}>
                        <TableCell>
                          <span className='text-sm'>
                            {formatDate(comment.date_from)}
                            {comment.date_to &&
                              comment.date_to !== comment.date_from && (
                                <> → {formatDate(comment.date_to)}</>
                              )}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className='text-sm'>
                            {comment.season || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className='text-sm'>
                            {comment.market_id || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {comment.variety ? (
                            <Chip size='sm' variant='flat'>
                              {comment.variety}
                            </Chip>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {comment.caliber ? (
                            <Chip size='sm' variant='flat'>
                              {comment.caliber}
                            </Chip>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {comment.color ? (
                            <Chip size='sm' variant='flat'>
                              {comment.color}
                            </Chip>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>{comment.seller || '-'}</TableCell>
                        <TableCell>
                          <span className='text-sm'>
                            {comment.label || '-'}
                          </span>
                          {(comment.format || comment.category) && (
                            <span className='block text-xs text-default-400'>
                              {[comment.format, comment.category].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className='text-sm'>
                            {comment.exporter_ref || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className='text-sm'>
                            {comment.container_number || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className='text-sm'>
                            {comment.ship_name || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className='text-sm'>
                            {formatFleet(comment.fleet)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {comment.unit_price != null &&
                          comment.unit_weight != null ? (
                            <div className='text-sm'>
                              <div className='font-medium'>
                                {comment.unit_price.toFixed(2)}{' '}
                                {comment.local_currency || ''}
                              </div>
                              <div className='text-xs text-default-500'>
                                {comment.unit_weight} kg
                                {comment.unit_count &&
                                  ` × ${comment.unit_count}`}
                              </div>
                            </div>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {comment.content ? (
                            <Tooltip
                              content={
                                <div className='max-w-[300px] p-2 whitespace-pre-wrap'>
                                  {comment.content}
                                </div>
                              }
                              placement='top'
                            >
                              <div className='max-w-[200px] truncate text-sm cursor-help'>
                                {comment.content}
                              </div>
                            </Tooltip>
                          ) : (
                            <span>-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className='text-sm text-default-600'>
                            {getCreatorName(comment)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className='text-xs text-default-500'>
                            {formatDateTime(comment.created_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className='text-xs text-default-500'>
                            {formatDateTime(comment.updated_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {comment.is_edited ? (
                            <Chip size='sm' variant='flat' color='success'>
                              Sí
                            </Chip>
                          ) : (
                            <span className='text-sm'>No</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {(typeof comment.created_by === 'object'
                            ? (comment.created_by as any).id
                            : comment.created_by) === userId && (
                            <div className='flex gap-1'>
                              <Tooltip content={t('Eliminar')}>
                                <Button
                                  isIconOnly
                                  size='sm'
                                  variant='light'
                                  color='danger'
                                  onPress={() => handleDelete(comment.id)}
                                >
                                  <Icon icon='tabler:trash' width={14} />
                                </Button>
                              </Tooltip>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardBody>
          </Card>
        )}
      </div>
    </PageLayout>
  );
};

export default LogbookPage;
